import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import { FileModel } from '../types/CommonTypes';
import { post } from './baseAxios';

const acceptedImageTypes = ['image/gif', 'image/jpeg', 'image/png', 'image/webp', 'image/svg+xml'];

const getHeightAndWidthFromFile = (file): Promise<{ width: number; height: number } | null> => {
  return new Promise((resolve) => {
    if (!file) {
      resolve(null);
      return;
    }

    try {
      const url = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => {
        resolve({
          height: img.height,
          width: img.width,
        });
      };
      img.onerror = () => {
        resolve(null);
      };
      img.src = url;
    } catch (e) {
      console.error('Fail to get width and height from file', e);
      resolve(null);
    }
  });
};

export const uploadMultiToFolder = async (
  folder: string,
  files: any[],
  userId: number,
  includeDimensions = false,
  skipResponsive = false
): Promise<any> => {
  const data: any = new FormData();
  data.append('folder', folder);
  const filenames: any[] = files.map((f) => f.name);
  const map = files.reduce((result, x) => {
    result[x.name] = x;
    return result;
  }, {});
  data.append('files', filenames);

  const { data: presignedUrls } = await post('/presigned-url', data);
  const uploadedFiles = await Promise.all(
    Object.keys(presignedUrls).map(async (name) => {
      const { key, url } = presignedUrls[name];
      const file = map[name];
      const options = { headers: { 'Content-Type': file.type } };
      await axios.put(url, file, options);

      if (!includeDimensions) {
        return { key, name };
      }

      let width;
      let height;
      if (acceptedImageTypes.includes(file.type)) {
        const dimensions = await getHeightAndWidthFromFile(file);
        if (dimensions) {
          width = dimensions.width;
          height = dimensions.height;
        }
      }
      return { key, name, width, height };
    })
  );

  return post('/presigned-url/ack', {
    folder,
    userId,
    files: uploadedFiles,
    skipResponsive,
  });
};

export const uploadFiles = (
  objectId: number | null,
  files: any[],
  name: string,
  userId: number,
  folderPath: string,
  includeDimensions = false,
  skipResponsive = false,
) => {
  let filesList = files.filter((f) => f.id);

  const newFiles = files.filter((f) => !f.id);
  if (newFiles && newFiles.length === 0) {
    return Promise.resolve({ name: name, filesList });
  }
  console.log('Files will be uploaded', newFiles);

  const idFolder = objectId ? `${folderPath}/${objectId}` : `${folderPath}/temp/${uuidv4()}`;

  return uploadMultiToFolder(idFolder, newFiles, userId, includeDimensions, skipResponsive)
    .then((res) => {
      const { data } = res;
      const uploadedFiles = Object.values(data.photoIds);

      const savedPhotos =
        files.filter((f) => f.id && uploadedFiles.findIndex((p: any) => p.id === f.id) < 0) || [];

      filesList = uploadedFiles.concat(savedPhotos);
      return { name: name, filesList };
    })
    .catch((err) => {
      console.error(err);
      return { name: name, filesList };
    });
};

export const convertFilesUploadDataToDto = (
  dto: any,
  filename: string,
  uploadResult: { filesList: FileModel[]; name: string },
  multiple = false
) => {
  if (!multiple) {
    const { filesList } = uploadResult;
    if (filesList.length) {
      const firstFile = filesList[0];
      dto[filename] = { id: firstFile.id, url: firstFile.url };
    } else {
      dto[filename] = undefined;
    }
  } else {
    const { filesList = [] } = uploadResult;
    if (filesList.length) {
      dto[filename] = filesList.map((file) => {
        return { id: file.id, url: file.url };
      });
    }
  }
};
